import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './UnitSort.css';

const UnitSort = () => {
  const navigate = useNavigate();
  const { isGuest } = useAuth();
  
  // 状态管理
  const [groups, setGroups] = useState({}); // 组别数据
  const [loading, setLoading] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState([]); // 选中的单位
  const [collapsedGroups, setCollapsedGroups] = useState({}); // 折叠状态

  // 检查登录状态
  const checkLogin = () => {
    if (isGuest) {
      navigate('/login');
      return false;
    }
    return true;
  };
  
  // 导入相关状态
  const [importText, setImportText] = useState('');
  const [groupName, setGroupName] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  
  // 识别相关状态
  const [recognizeText, setRecognizeText] = useState('');
  const [recognizeResult, setRecognizeResult] = useState(null);
  const [showRecognizeModal, setShowRecognizeModal] = useState(false);
  const [unmatchedSelections, setUnmatchedSelections] = useState({}); // 未匹配的手动选择
  const [unitSearchText, setUnitSearchText] = useState({}); // 单位搜索文本
  const [openDropdown, setOpenDropdown] = useState(null); // 当前打开的下拉框
  
  // 编辑相关状态
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editUnits, setEditUnits] = useState('');

  // 单位编辑相关状态
  const [editingUnit, setEditingUnit] = useState(null); // {groupId, unitIndex}
  const [editUnitName, setEditUnitName] = useState('');
  const [insertingUnit, setInsertingUnit] = useState(null); // {groupId, unitIndex}

  // 排序结果
  const [sortedText, setSortedText] = useState('');
  const [showResult, setShowResult] = useState(false);

  // 主题
  const [unitSortTitle, setUnitSortTitle] = useState('');

  // 历史记录
  const [historyList, setHistoryList] = useState([]);
  const [loadedHistoryId, setLoadedHistoryId] = useState(null);
  const loadedHistoryIdRef = useRef(null);
  const [historyPanelCollapsed, setHistoryPanelCollapsed] = useState(false);
  // 保持 ref 与 state 同步
  const updateLoadedHistoryId = (id) => {
    setLoadedHistoryId(id);
    loadedHistoryIdRef.current = id;
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // 获取认证头
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // 加载组别数据
  const loadGroups = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/unit/groups`, {
        headers: getAuthHeaders()
      });
      const groupsData = response.data.data;
      setGroups(groupsData);
      
      // 默认所有组别都折叠
      const initialCollapsed = {};
      Object.keys(groupsData).forEach(groupId => {
        initialCollapsed[groupId] = true;
      });
      setCollapsedGroups(initialCollapsed);
    } catch (error) {
      if (error.response?.status === 401) {
        alert('登录已过期，请重新登录');
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
  };

  useEffect(() => {
    loadGroups();
    loadHistory();
  }, []);

  // 批量导入单位
  const handleImport = async () => {
    if (!checkLogin()) return;
    if (!importText.trim()) {
      alert('请输入单位名称');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/unit/import`,
        { text: importText, group_name: groupName },
        { headers: getAuthHeaders() }
      );
      
      alert(`成功导入 ${response.data.count} 个单位`);
      setImportText('');
      setGroupName('');
      setShowImportModal(false);
      await loadGroups();
    } catch (error) {
      alert(error.response?.data?.detail || '导入失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除组别
  const handleDeleteGroup = async (groupId) => {
    if (!checkLogin()) return;
    if (!window.confirm('确定要删除该组别吗？')) return;

    const deletedUnits = groups[groupId].units;

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/unit/group/${groupId}`, {
        headers: getAuthHeaders()
      });

      // 从选中列表中移除已删除组别下的所有单位
      setSelectedUnits(prev => prev.filter(u => !deletedUnits.includes(u)));

      await loadGroups();
    } catch (error) {
      alert(error.response?.data?.detail || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  // 开始编辑组别
  const startEditGroup = (groupId) => {
    const group = groups[groupId];
    setEditingGroup(groupId);
    setEditGroupName(group.name);
    setEditUnits(group.units.join('\n'));
  };

  // 保存编辑
  const handleSaveEdit = async () => {
    if (!checkLogin()) return;

    const units = editUnits.split('\n').filter(u => u.trim());
    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/unit/group/${editingGroup}`,
        { name: editGroupName, units },
        { headers: getAuthHeaders() }
      );
      setEditingGroup(null);
      await loadGroups();
    } catch (error) {
      alert(error.response?.data?.detail || '保存失败');
    } finally {
      setLoading(false);
    }
  };

  // 上移组别
  const handleMoveGroupUp = async (groupId) => {
    const groupIds = Object.keys(groups);
    const index = groupIds.indexOf(groupId);
    if (index <= 0) return;

    // 交换顺序
    [groupIds[index - 1], groupIds[index]] = [groupIds[index], groupIds[index - 1]];
    
    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/unit/reorder-groups`,
        groupIds,
        { headers: getAuthHeaders() }
      );
      await loadGroups();
    } catch (error) {
      alert('调整顺序失败');
    } finally {
      setLoading(false);
    }
  };

  // 下移组别
  const handleMoveGroupDown = async (groupId) => {
    const groupIds = Object.keys(groups);
    const index = groupIds.indexOf(groupId);
    if (index >= groupIds.length - 1) return;

    // 交换顺序
    [groupIds[index], groupIds[index + 1]] = [groupIds[index + 1], groupIds[index]];

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/unit/reorder-groups`,
        groupIds,
        { headers: getAuthHeaders() }
      );
      await loadGroups();
    } catch (error) {
      alert('调整顺序失败');
    } finally {
      setLoading(false);
    }
  };

  // 上移单位
  const handleMoveUnitUp = async (groupId, unitIndex) => {
    if (unitIndex <= 0) return;

    const newUnits = [...groups[groupId].units];
    [newUnits[unitIndex - 1], newUnits[unitIndex]] = [newUnits[unitIndex], newUnits[unitIndex - 1]];

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/unit/group/${groupId}`,
        { name: groups[groupId].name, units: newUnits },
        { headers: getAuthHeaders() }
      );
      await loadGroups();
    } catch (error) {
      alert('调整顺序失败');
    } finally {
      setLoading(false);
    }
  };

  // 下移单位
  const handleMoveUnitDown = async (groupId, unitIndex) => {
    const units = groups[groupId].units;
    if (unitIndex >= units.length - 1) return;

    const newUnits = [...units];
    [newUnits[unitIndex], newUnits[unitIndex + 1]] = [newUnits[unitIndex + 1], newUnits[unitIndex]];

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/unit/group/${groupId}`,
        { name: groups[groupId].name, units: newUnits },
        { headers: getAuthHeaders() }
      );
      await loadGroups();
    } catch (error) {
      alert('调整顺序失败');
    } finally {
      setLoading(false);
    }
  };

  // 删除单位
  const handleDeleteUnit = async (groupId, unitIndex) => {
    if (!window.confirm('确定要删除该单位吗？')) return;

    const deletedUnitName = groups[groupId].units[unitIndex];
    const newUnits = groups[groupId].units.filter((_, idx) => idx !== unitIndex);

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/unit/group/${groupId}`,
        { name: groups[groupId].name, units: newUnits },
        { headers: getAuthHeaders() }
      );

      // 从选中列表中移除已删除的单位
      if (selectedUnits.includes(deletedUnitName)) {
        setSelectedUnits(prev => prev.filter(u => u !== deletedUnitName));
      }

      await loadGroups();
    } catch (error) {
      alert('删除失败');
    } finally {
      setLoading(false);
    }
  };

  // 开始编辑单位
  const startEditUnit = (groupId, unitIndex) => {
    setEditingUnit({ groupId, unitIndex });
    setEditUnitName(groups[groupId].units[unitIndex]);
    setInsertingUnit(null);
  };

  // 保存编辑单位
  const handleSaveEditUnit = async () => {
    if (!editUnitName.trim()) {
      alert('单位名称不能为空');
      return;
    }

    const { groupId, unitIndex } = editingUnit;
    const oldUnitName = groups[groupId].units[unitIndex];
    const newUnitName = editUnitName.trim();
    const newUnits = [...groups[groupId].units];
    newUnits[unitIndex] = newUnitName;

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/unit/group/${groupId}`,
        { name: groups[groupId].name, units: newUnits },
        { headers: getAuthHeaders() }
      );

      // 如果该单位已被选中，更新选中列表中的名称
      if (selectedUnits.includes(oldUnitName)) {
        setSelectedUnits(prev =>
          prev.map(u => u === oldUnitName ? newUnitName : u)
        );
      }

      setEditingUnit(null);
      setEditUnitName('');
      await loadGroups();
    } catch (error) {
      alert('保存失败');
    } finally {
      setLoading(false);
    }
  };

  // 取消编辑单位
  const cancelEditUnit = () => {
    setEditingUnit(null);
    setEditUnitName('');
  };

  // 插入单位
  const startInsertUnit = (groupId, unitIndex) => {
    setInsertingUnit({ groupId, unitIndex });
    setEditUnitName('');
    setEditingUnit(null);
  };

  // 保存插入单位
  const handleSaveInsertUnit = async () => {
    if (!editUnitName.trim()) {
      alert('单位名称不能为空');
      return;
    }

    const { groupId, unitIndex } = insertingUnit;
    const newUnits = [...groups[groupId].units];
    newUnits.splice(unitIndex, 0, editUnitName.trim());

    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/unit/group/${groupId}`,
        { name: groups[groupId].name, units: newUnits },
        { headers: getAuthHeaders() }
      );
      setInsertingUnit(null);
      setEditUnitName('');
      await loadGroups();
    } catch (error) {
      alert('插入失败');
    } finally {
      setLoading(false);
    }
  };

  // 取消插入单位
  const cancelInsertUnit = () => {
    setInsertingUnit(null);
    setEditUnitName('');
  };

  // 切换组别折叠状态
  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // 选择/取消选择单位
  const toggleUnit = (unit) => {
    setSelectedUnits(prev => {
      if (prev.includes(unit)) {
        return prev.filter(u => u !== unit);
      } else {
        return [...prev, unit];
      }
    });
  };

  // 全选组别内所有单位
  const toggleGroupUnits = (groupUnits) => {
    const allSelected = groupUnits.every(u => selectedUnits.includes(u));
    if (allSelected) {
      setSelectedUnits(prev => prev.filter(u => !groupUnits.includes(u)));
    } else {
      setSelectedUnits(prev => [...new Set([...prev, ...groupUnits])]);
    }
  };

  // 识别文本中的单位
  const handleRecognize = async () => {
    if (!checkLogin()) return;
    if (!recognizeText.trim()) {
      alert('请输入要识别的文本');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/unit/recognize`,
        { text: recognizeText },
        { headers: getAuthHeaders() }
      );
      setRecognizeResult(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || '识别失败');
    } finally {
      setLoading(false);
    }
  };

  // 确认识别结果
  const confirmRecognize = () => {
    const matchedUnits = [];
    
    // 添加自动匹配的单位
    if (recognizeResult?.matched) {
      matchedUnits.push(...recognizeResult.matched.map(m => m.matched));
    }
    
    // 添加手动选择的匹配
    Object.values(unmatchedSelections).forEach(unit => {
      if (unit) {
        matchedUnits.push(unit);
      }
    });
    
    setSelectedUnits(prev => [...new Set([...prev, ...matchedUnits])]);
    setRecognizeText('');
    setRecognizeResult(null);
    setUnmatchedSelections({});
    setShowRecognizeModal(false);
  };

  // 获取所有单位列表
  const getAllUnits = () => {
    const allUnits = [];
    Object.values(groups).forEach(group => {
      allUnits.push(...group.units);
    });
    return allUnits;
  };

  // 处理未匹配单位的手动选择
  const handleUnmatchedSelect = (unmatchedItem, selectedUnit) => {
    setUnmatchedSelections(prev => ({
      ...prev,
      [unmatchedItem]: selectedUnit
    }));
  };

  // 删除未匹配项
  const removeUnmatchedItem = (item) => {
    setRecognizeResult(prev => ({
      ...prev,
      unmatched: prev.unmatched.filter(u => u !== item)
    }));
    setUnmatchedSelections(prev => {
      const newSelections = { ...prev };
      delete newSelections[item];
      return newSelections;
    });
    setUnitSearchText(prev => {
      const newSearch = { ...prev };
      delete newSearch[item];
      return newSearch;
    });
  };

  // 过滤单位列表
  const getFilteredUnits = (searchText) => {
    const allUnits = getAllUnits();
    if (!searchText) return allUnits;
    return allUnits.filter(unit =>
      unit.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // 执行排序
  const handleSort = async () => {
    if (!checkLogin()) return;
    if (selectedUnits.length === 0) {
      alert('请先选择单位');
      return;
    }

    setLoading(true);
    try {
      // 先重新加载最新的组别数据，确保使用最新数据
      const groupsResponse = await axios.get(`${API_BASE_URL}/api/unit/groups`, {
        headers: getAuthHeaders()
      });
      const latestGroups = groupsResponse.data.data;

      // 过滤掉不在最新组别数据中的单位
      const allLatestUnits = [];
      Object.values(latestGroups).forEach(group => {
        allLatestUnits.push(...group.units);
      });
      const validSelectedUnits = selectedUnits.filter(u => allLatestUnits.includes(u));

      if (validSelectedUnits.length === 0) {
        alert('没有有效的选中单位');
        setLoading(false);
        return;
      }

      // 调用排序API
      const response = await axios.post(
        `${API_BASE_URL}/api/unit/sort`,
        { selected_units: validSelectedUnits },
        { headers: getAuthHeaders() }
      );

      // 生成排序文本
      const sorted = response.data.sorted_units;

      // 按组别分组（使用最新数据）
      const unitInGroup = {};
      for (const [groupId, groupData] of Object.entries(latestGroups)) {
        const groupUnits = sorted.filter(u => groupData.units.includes(u));
        if (groupUnits.length > 0) {
          unitInGroup[groupData.name] = groupUnits;
        }
      }

      // 按组别顺序生成文本（使用最新数据）
      const parts = [];
      for (const groupData of Object.values(latestGroups)) {
        if (unitInGroup[groupData.name]) {
          parts.push(unitInGroup[groupData.name].join('、'));
        }
      }

      setSortedText(parts.join('，'));
      setShowResult(true);

      // 更新本地groups状态
      setGroups(latestGroups);

      // 自动保存历史记录（传入当前值避免闭包问题）
      autoSaveHistory(loadedHistoryIdRef.current, validSelectedUnits, unitSortTitle);
    } catch (error) {
      alert(error.response?.data?.detail || '排序失败');
    } finally {
      setLoading(false);
    }
  };

  // 生成Word文档
  const handleGenerateDoc = async () => {
    if (!checkLogin()) return;
    if (selectedUnits.length === 0) {
      alert('请先选择单位');
      return;
    }

    setLoading(true);
    try {
      // 先重新加载最新的组别数据
      const groupsResponse = await axios.get(`${API_BASE_URL}/api/unit/groups`, {
        headers: getAuthHeaders()
      });
      const latestGroups = groupsResponse.data.data;

      // 过滤掉不在最新组别数据中的单位
      const allLatestUnits = [];
      Object.values(latestGroups).forEach(group => {
        allLatestUnits.push(...group.units);
      });
      const validSelectedUnits = selectedUnits.filter(u => allLatestUnits.includes(u));

      if (validSelectedUnits.length === 0) {
        alert('没有有效的选中单位');
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/unit/generate`,
        { selected_units: validSelectedUnits, title: unitSortTitle.trim() || '' },
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `${unitSortTitle.trim() || '发文单位'}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // 更新本地groups状态
      setGroups(latestGroups);

      // 自动保存历史记录（传入当前值避免闭包问题）
      autoSaveHistory(loadedHistoryIdRef.current, validSelectedUnits, unitSortTitle);
    } catch (error) {
      alert(error.response?.data?.detail || '生成失败');
    } finally {
      setLoading(false);
    }
  };

  // 清空选择
  const clearSelection = () => {
    setSelectedUnits([]);
    setSortedText('');
    setShowResult(false);
  };

  // ==================== 历史记录功能 ====================

  // 加载历史记录列表
  const loadHistory = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setHistoryList([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/unit/history`, {
        headers: getAuthHeaders()
      });
      setHistoryList(response.data.data || []);
    } catch (error) {
      console.error('加载历史记录失败:', error);
    }
  };

  // 保存到历史记录
  const handleSaveHistory = async () => {
    if (!checkLogin()) return;
    const title = unitSortTitle.trim() || null;
    try {
      await axios.post(`${API_BASE_URL}/api/unit/save?title=${encodeURIComponent(title || '')}`, { selected_units: selectedUnits }, {
        headers: getAuthHeaders()
      });
      alert('保存成功');
      loadHistory();
      updateLoadedHistoryId(null);
    } catch (error) {
      alert(error.response?.data?.detail || '保存失败');
    }
  };

  // 自动保存历史记录（静默保存，不弹窗提示）
  const autoSaveHistory = async (currentLoadedId, currentSelectedUnits, currentTitle) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    const title = (currentTitle || '').trim() || null;
    const saveBody = { selected_units: currentSelectedUnits };
    try {
      if (currentLoadedId) {
        // 更新已加载的历史记录
        await axios.put(
          `${API_BASE_URL}/api/unit/history/${currentLoadedId}?title=${encodeURIComponent(title || '')}`,
          saveBody,
          { headers: getAuthHeaders() }
        );
      } else {
        // 创建新的历史记录
        const res = await axios.post(`${API_BASE_URL}/api/unit/save?title=${encodeURIComponent(title || '')}`, saveBody, {
          headers: getAuthHeaders()
        });
        if (res.data?.data?.id) {
          updateLoadedHistoryId(res.data.data.id);
        }
      }
      loadHistory();
    } catch (error) {
      console.error('自动保存历史记录失败:', error);
    }
  };

  // 加载历史记录
  const handleLoadHistory = async (historyId) => {
    if (!checkLogin()) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/api/unit/history/${historyId}`, {
        headers: getAuthHeaders()
      });
      const data = response.data.data;
      setGroups(data.groups);
      setUnitSortTitle(data.title || '');
      updateLoadedHistoryId(historyId);
      setSelectedUnits(data.selected_units || []);
      setSortedText('');
      setShowResult(false);
      // 加载后展开所有组别
      const newCollapsed = {};
      Object.keys(data.groups).forEach(gId => {
        newCollapsed[gId] = false;
      });
      setCollapsedGroups(newCollapsed);
    } catch (error) {
      alert(error.response?.data?.detail || '加载失败');
    }
  };

  // 删除历史记录
  const handleDeleteHistory = async (historyId) => {
    if (!checkLogin()) return;
    if (!window.confirm('确定要删除这条历史记录吗？')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/unit/history/${historyId}`, {
        headers: getAuthHeaders()
      });
      loadHistory();
      if (loadedHistoryId === historyId) {
        updateLoadedHistoryId(null);
      }
    } catch (error) {
      alert(error.response?.data?.detail || '删除失败');
    }
  };

  // 统计
  const totalUnits = Object.values(groups).reduce((sum, g) => sum + g.units.length, 0);

  return (
    <div className="unit-sort-container">
      <div className="unit-sort-content">
        {/* 左侧：历史记录 + 单位列表 */}
        <div className="unit-left-panel">
          {/* 历史记录栏 */}
          <div className="history-panel">
            <div className="panel-header history-panel-header" onClick={() => setHistoryPanelCollapsed(!historyPanelCollapsed)}>
              <h3>历史记录</h3>
              <button className="toggle-history-btn" onClick={(e) => { e.stopPropagation(); setHistoryPanelCollapsed(!historyPanelCollapsed); }}>{historyPanelCollapsed ? '▶' : '▼'}</button>
            </div>
            {!historyPanelCollapsed && <div className="history-list">
              {historyList.length === 0 ? (
                <div className="empty-history">暂无历史记录</div>
              ) : (
                historyList.map((item) => (
                  <div key={item.id} className={`history-item ${loadedHistoryId === item.id ? 'loaded' : ''}`}>
                    <div className="history-title-row">
                      <div className="history-title">{item.title}</div>
                      <div className="history-actions">
                        <button className="load-btn" onClick={() => handleLoadHistory(item.id)} disabled={!isGuest && loading}>加载</button>
                        <button className="delete-history-btn" onClick={() => handleDeleteHistory(item.id)} disabled={!isGuest && loading}>删除</button>
                      </div>
                    </div>
                    <div className="history-time">{item.updated_at || item.created_at}</div>
                  </div>
                ))
              )}
            </div>
            }

          </div>
          <div className="unit-list-panel">
            <div className="panel-header">
              <h3>单位列表</h3>
              <button 
                className="action-btn primary"
                onClick={() => setShowImportModal(true)}
              >
                + 导入单位
              </button>
            </div>

          <div className="panel-body">
            {Object.keys(groups).length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📁</div>
                <p>暂无单位数据</p>
                <p className="hint">点击"导入单位"开始添加</p>
              </div>
            ) : (
              <div className="groups-list">
                {Object.entries(groups).map(([groupId, groupData], index, arr) => (
                  <div key={groupId} className="group-item">
                    <div className="group-header" onClick={() => toggleGroupCollapse(groupId)}>
                      <div className="group-info">
                        <div className="group-name-row">
                          <span className="collapse-icon">{collapsedGroups[groupId] ? '▶' : '▼'}</span>
                          <span className="group-name">{groupData.name}</span>
                        </div>
                        <span className="group-count">({groupData.units.length}个单位)</span>
                      </div>
                      <div className="group-actions" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="icon-btn"
                          onClick={() => handleMoveGroupUp(groupId)}
                          disabled={index === 0}
                          title="上移"
                        >
                          ↑
                        </button>
                        <button 
                          className="icon-btn"
                          onClick={() => handleMoveGroupDown(groupId)}
                          disabled={index === arr.length - 1}
                          title="下移"
                        >
                          ↓
                        </button>
                        <button 
                          className="icon-btn edit"
                          onClick={() => startEditGroup(groupId)}
                          title="编辑"
                        >
                          ✏️
                        </button>
                        <button 
                          className="icon-btn delete"
                          onClick={() => handleDeleteGroup(groupId)}
                          title="删除"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    {!collapsedGroups[groupId] && (
                      editingGroup === groupId ? (
                        <div className="edit-form">
                          <input
                            type="text"
                            value={editGroupName}
                            onChange={(e) => setEditGroupName(e.target.value)}
                            placeholder="组别名称"
                          />
                          <textarea
                            value={editUnits}
                            onChange={(e) => setEditUnits(e.target.value)}
                            placeholder="单位名称（每行一个）"
                            rows={5}
                          />
                          <div className="edit-actions">
                            <button className="btn-save" onClick={handleSaveEdit}>保存</button>
                            <button className="btn-cancel" onClick={() => setEditingGroup(null)}>取消</button>
                          </div>
                        </div>
                      ) : (
                        <div className="units-list">
                          <div className="select-all">
                            <label>
                              <input
                                type="checkbox"
                                checked={groupData.units.every(u => selectedUnits.includes(u))}
                                onChange={() => toggleGroupUnits(groupData.units)}
                              />
                              全选
                            </label>
                          </div>
                          {groupData.units.map((unit, unitIndex) => (
                            <React.Fragment key={unitIndex}>
                              {/* 插入单位编辑框 */}
                              {insertingUnit?.groupId === groupId && insertingUnit?.unitIndex === unitIndex && (
                                <div className="insert-unit-form">
                                  <input
                                    type="text"
                                    value={editUnitName}
                                    onChange={(e) => setEditUnitName(e.target.value)}
                                    placeholder="输入单位名称"
                                    autoFocus
                                  />
                                  <div className="insert-unit-actions">
                                    <button className="btn-save-small" onClick={handleSaveInsertUnit}>保存</button>
                                    <button className="btn-cancel-small" onClick={cancelInsertUnit}>取消</button>
                                  </div>
                                </div>
                              )}

                              {/* 单位项 */}
                              <div className={`unit-item ${selectedUnits.includes(unit) ? 'selected' : ''}`}>
                                <div className="unit-checkbox" onClick={(e) => { e.stopPropagation(); toggleUnit(unit); }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedUnits.includes(unit)}
                                    onChange={() => toggleUnit(unit)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>

                                {editingUnit?.groupId === groupId && editingUnit?.unitIndex === unitIndex ? (
                                  <div className="edit-unit-form">
                                    <input
                                      type="text"
                                      value={editUnitName}
                                      onChange={(e) => setEditUnitName(e.target.value)}
                                      placeholder="输入单位名称"
                                      autoFocus
                                    />
                                    <div className="edit-unit-actions">
                                      <button className="btn-save-small" onClick={handleSaveEditUnit}>保存</button>
                                      <button className="btn-cancel-small" onClick={cancelEditUnit}>取消</button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <span className="unit-name" onClick={() => toggleUnit(unit)}>{unit}</span>
                                    <div className="unit-actions">
                                      <button
                                        className="icon-btn-small"
                                        onClick={() => handleMoveUnitUp(groupId, unitIndex)}
                                        disabled={unitIndex === 0}
                                        title="上移"
                                      >
                                        ↑
                                      </button>
                                      <button
                                        className="icon-btn-small insert"
                                        onClick={() => startInsertUnit(groupId, unitIndex)}
                                        title="插入"
                                      >
                                        +
                                      </button>
                                      <button
                                        className="icon-btn-small"
                                        onClick={() => handleMoveUnitDown(groupId, unitIndex)}
                                        disabled={unitIndex === groupData.units.length - 1}
                                        title="下移"
                                      >
                                        ↓
                                      </button>
                                      <button
                                        className="icon-btn-small edit"
                                        onClick={() => startEditUnit(groupId, unitIndex)}
                                        title="编辑"
                                      >
                                        ✏️
                                      </button>
                                      <button
                                        className="icon-btn-small delete"
                                        onClick={() => handleDeleteUnit(groupId, unitIndex)}
                                        title="删除"
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="panel-footer">
            <div className="stats">
              共 {Object.keys(groups).length} 个组别，{totalUnits} 个单位
            </div>
          </div>
        </div>
        </div>

        {/* 右侧：操作区 */}
        <div className="operation-panel">
          <div className="panel-header">
            <h3>排序操作</h3>
          </div>

          <div className="panel-body">
            {/* 主题输入 */}
            <div className="title-section">
              <h4>主题</h4>
              <input
                type="text"
                className="title-input"
                value={unitSortTitle}
                onChange={(e) => setUnitSortTitle(e.target.value)}
                placeholder="输入发文单位排序的主题（用于文件命名）"
              />
            </div>

            {/* 文本识别 */}
            <div className="recognize-section">
              <h4>文本识别选择</h4>
              <p className="hint">输入包含单位名称的文本，系统将自动识别并匹配</p>
              <button 
                className="action-btn primary"
                onClick={() => setShowRecognizeModal(true)}
              >
                🔍 识别选择
              </button>
            </div>

            {/* 已选单位 */}
            <div className="selected-section">
              <div className="section-header">
                <h4>已选单位 ({selectedUnits.length})</h4>
                {selectedUnits.length > 0 && (
                  <button className="text-btn" onClick={clearSelection}>清空</button>
                )}
              </div>
              <div className="selected-units">
                {selectedUnits.length === 0 ? (
                  <div className="usage-guide-placeholder">
                    <h4>📖 使用说明</h4>
                    <ul>
                      <li>点击"导入单位"批量添加单位名称，支持多种分隔符</li>
                      <li>可以创建多个组别，如"市直单位"、"县区单位"等</li>
                      <li>通过拖拽或上下箭头调整组别和单位的顺序</li>
                      <li>从列表中选择需要排序的单位，或使用"识别选择"智能匹配</li>
                      <li>点击"排序"按组别顺序生成排序文本，组间用"，"分隔，组内用"、"分隔</li>
                      <li>点击"生成文档"导出Word格式的发文单位列表</li>
                    </ul>
                  </div>
                ) : (
                  <div className="selected-tags">
                    {selectedUnits.map((unit, index) => (
                      <span 
                        key={index} 
                        className="unit-tag"
                        onClick={() => toggleUnit(unit)}
                      >
                        {unit} ×
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="action-buttons">
              <button 
                className="action-btn primary large"
                onClick={handleSort}
                disabled={!isGuest && (loading || selectedUnits.length === 0)}
              >
                📊 排序
              </button>
              <button 
                className="action-btn success large"
                onClick={handleGenerateDoc}
                disabled={!isGuest && (loading || selectedUnits.length === 0)}
              >
                📄 生成文档
              </button>
            </div>

            {/* 排序结果 */}
            {showResult && (
              <div className="result-section">
                <h4>排序结果</h4>
                <div className="result-text">{sortedText}</div>
                <button 
                  className="text-btn"
                  onClick={() => navigator.clipboard.writeText(sortedText).then(() => alert('已复制到剪贴板'))}
                >
                  📋 复制文本
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 导入弹窗 */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3>导入单位名称</h3>
              <button className="close-btn" onClick={() => setShowImportModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>组别名称（可选）</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="如：市直单位、县区单位等"
                />
              </div>
              <div className="form-group">
                <label>单位名称</label>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="支持多种格式：&#10;&#10;1. 每行一个单位名称&#10;百色市大数据发展局&#10;市委办公室&#10;&#10;2. 用顿号分隔&#10;百色市大数据发展局、市委办公室、市政府办公室&#10;&#10;3. 用逗号分隔&#10;百色市大数据发展局，市委办公室，市政府办公室"
                  rows={10}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowImportModal(false)}>取消</button>
              <button className="btn-primary" onClick={handleImport} disabled={!isGuest && loading}>
                {loading ? '导入中...' : '导入'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 识别弹窗 */}
      {showRecognizeModal && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3>识别单位名称</h3>
              <button className="close-btn" onClick={() => {
                setShowRecognizeModal(false);
                setRecognizeResult(null);
                setUnmatchedSelections({});
                setUnitSearchText({});
              }}>×</button>
            </div>
            <div className="modal-body" onClick={() => setOpenDropdown(null)}>
              <div className="form-group">
                <label>输入文本</label>
                <textarea
                  value={recognizeText}
                  onChange={(e) => setRecognizeText(e.target.value)}
                  placeholder="粘贴包含单位名称的文本，系统将自动识别匹配"
                  rows={5}
                />
              </div>
              
              <button 
                className="btn-primary full-width"
                onClick={handleRecognize}
                disabled={!isGuest && loading}
              >
                {loading ? '识别中...' : '🔍 识别'}
              </button>

              {recognizeResult && (
                <div className="recognize-result">
                  {recognizeResult.matched.length > 0 && (
                    <div className="matched-section">
                      <h4>匹配成功 ({recognizeResult.matched.length})</h4>
                      <div className="matched-list">
                        {recognizeResult.matched.map((item, index) => (
                          <div key={index} className="matched-item">
                            <span className="input-text">{item.input}</span>
                            <span className="arrow">→</span>
                            <span className="matched-text">{item.matched}</span>
                            <span className="score">{Math.round(item.score)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {recognizeResult.unmatched.length > 0 && (
                    <div className="unmatched-section">
                      <h4>未匹配 ({recognizeResult.unmatched.length}) - 可手动选择</h4>
                      <div className="unmatched-list">
                        {recognizeResult.unmatched.map((item, index) => (
                          <div key={index} className="unmatched-item-wrapper">
                            <span className="unmatched-item">{item}</span>
                            <div className="unmatched-select-wrapper">
                              <div
                                className="unmatched-select-trigger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdown(openDropdown === item ? null : item);
                                }}
                              >
                                <span className={unmatchedSelections[item] ? 'selected' : 'placeholder'}>
                                  {unmatchedSelections[item] || '-- 选择匹配单位 --'}
                                </span>
                                <span className="dropdown-arrow">{openDropdown === item ? '▲' : '▼'}</span>
                              </div>
                              {openDropdown === item && (
                                <div className="unmatched-dropdown" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    className="unmatched-search"
                                    placeholder="搜索单位..."
                                    value={unitSearchText[item] || ''}
                                    onChange={(e) => setUnitSearchText(prev => ({
                                      ...prev,
                                      [item]: e.target.value
                                    }))}
                                    autoFocus
                                  />
                                  <div className="unmatched-options">
                                    {getFilteredUnits(unitSearchText[item]).length === 0 ? (
                                      <div className="no-options">无匹配单位</div>
                                    ) : (
                                      getFilteredUnits(unitSearchText[item]).map((unit, idx) => (
                                        <div
                                          key={idx}
                                          className={`unmatched-option ${unmatchedSelections[item] === unit ? 'active' : ''}`}
                                          onClick={() => {
                                            handleUnmatchedSelect(item, unit);
                                            setOpenDropdown(null);
                                            setUnitSearchText(prev => ({ ...prev, [item]: '' }));
                                          }}
                                        >
                                          {unit}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              className="remove-unmatched-btn"
                              onClick={() => removeUnmatchedItem(item)}
                              title="删除"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => {
                setShowRecognizeModal(false);
                setRecognizeResult(null);
                setUnmatchedSelections({});
                setUnitSearchText({});
              }}>取消</button>
              <button
                className="btn-primary"
                onClick={confirmRecognize}
                disabled={!isGuest && (!recognizeResult || (recognizeResult.matched.length === 0 && Object.keys(unmatchedSelections).length === 0))}
              >
                确认选择
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitSort;
